import { API_CALL, LERE_DISMISS_ALL_STICKY_ABANDONED_CART, LERE_REMOVE_ABANDONED_CART, LERE_REMOVE_HIGH_INTENT, LERE_SAVE_ABANDONED_CART, LERE_SAVE_HOT_LEADS } from 'actions/actionConstants'
import { FETCH_CALENDAR_OFFER_RECOMMENDATIONS, FETCH_LERE_HIGH_INTENT, FETCH_LERE_PERSONALISED_PEOPLE_LIKE_ME, FETCH_LERE_PERSONALISED_PREFERENCES, FETCH_LERE_TOP_DEALS, FETCH_LERE_TOUR_RECOMMENDATIONS, FETCH_LATEST_RESTORABLE_CHECKOUT_CART, FETCH_LERE_USER_INTERACTIONS, FETCH_OFFER_BASED_LERE_RECOMMENDATIONS, FETCH_URGENT_OFFER_ROOM_DATES } from 'actions/apiActionConstants'
import * as RecommendationService from 'api/recommendations'
import { getLatestRestorableCheckoutCart } from 'api/cart/getLatestRestorableCheckoutCart'
import config from 'constants/config'
import { OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_HOTEL } from 'constants/offer'
import { timeNowInSecond } from 'lib/datetime/time'
import { getCanViewLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { getCurrentUserId, selectRecDomainUserId } from 'selectors/accountSelectors'
import { isSpoofed } from 'selectors/isSpoofed'
import { canUseConsentPurposeOnClient } from '../consent/waitingForConsent'
import type { AppAction } from './types/ActionTypes'

/**
 *
 * @param offerId offer id
 * @param checkIn Check in date in ISO format
 * @param checkOut Check out date in ISO format
 * @returns
 */
export function fetchOfferBasedRecommendations(offerId: string, allowMultiOffersSameProperty: boolean = false, checkIn?: string, checkOut?: string, occupancy?: string): AppAction {
  return async(dispatch, getState) => {
    // if we have one of these 2 params, we need to make sure both, otherwise skip the request
    if (checkIn || checkOut) {
      if (!(checkIn && checkOut)) {
        return
      }
    }

    const state = getState()
    const cacheKey = offerId

    const filterKey = (!!checkIn && !!checkOut && !!occupancy) ? [checkIn, checkOut, occupancy, allowMultiOffersSameProperty].join(';') : allowMultiOffersSameProperty.toString()

    const recommendationsState = state.recommendations.recommendations[cacheKey]

    if (recommendationsState?.filterKey === filterKey) {
      return
    }

    const canViewLuxPlusBenefits = getCanViewLuxPlusBenefits(state)

    dispatch({
      type: API_CALL,
      api: FETCH_OFFER_BASED_LERE_RECOMMENDATIONS,
      request: async() => {
        const result = await RecommendationService.getRecommendations(offerId, state.geo.currentRegionCode, canViewLuxPlusBenefits, allowMultiOffersSameProperty, checkIn, checkOut, occupancy)
        const { offers } = result

        const recommendationOfferIds = offers.map((r) => r.offerId)

        return recommendationOfferIds
      },
      cacheKey,
      filterKey,
    })
  }
}

export function fetchCalendarRecommendations(offerId: string, filters: {
  checkIn?: string
  availableMonth?: string
  rooms?: Array<App.Occupants>
  duration?: number
}): AppAction {
  return async(dispatch, getState) => {
    const state = getState()
    const requestKey = RecommendationService.getCalendarRecommendationsRequestKey(
      offerId,
      filters.checkIn,
      filters.availableMonth,
      filters.duration,
      filters.rooms,
    )
    const current = state.recommendations.calendarRecommendations
    if (current.requestKey === requestKey && (current.state === 'loading' || current.state === 'done')) {
      return
    }

    const canViewLuxPlusBenefits = getCanViewLuxPlusBenefits(state)

    dispatch({
      type: API_CALL,
      api: FETCH_CALENDAR_OFFER_RECOMMENDATIONS,
      request: async() => {
        const params = {
          checkIn: filters.checkIn,
          availableMonth: filters.availableMonth,
          rooms: filters.rooms,
          duration: filters.duration,
        }

        const { offers } = await RecommendationService.getCalendarRecommendations(offerId, state.geo.currentRegionCode, canViewLuxPlusBenefits, params)
        const recommendationOfferIds = offers.map((r) => r.offerId)

        return {
          offerIds: recommendationOfferIds,
        }
      },
      offerId,
      requestKey,
    })
  }
}

export function fetchPeopleLikeMeRecommendations(): AppAction {
  return async(dispatch, getState) => {
    if (!canUseConsentPurposeOnClient('personalisation')) { return }

    const state = getState()
    const currentState = state.recommendations.personalisedPeopleLikeMe.state

    if (currentState === 'loading') { return }

    const leUserId = state.auth.account.memberId
    const canViewLuxPlusBenefits = getCanViewLuxPlusBenefits(state)
    const region = state.geo.currentRegionCode

    // Work around for conditional dispatch
    let request = () => new Promise<{
      offers: Array<App.RecommendationOffer>
      lereVersion: string
    }>((resolve) => resolve({ offers: [], lereVersion: '' }))
    if (leUserId) {
      request = () => RecommendationService.getPeopleLikeMeRecommendations(leUserId, region, canViewLuxPlusBenefits)
    }

    dispatch({
      type: API_CALL,
      api: FETCH_LERE_PERSONALISED_PEOPLE_LIKE_ME,
      request,
    })
  }
}

export function fetchTopDeals(region: string): AppAction {
  return async(dispatch, getState) => {
    const state = getState()

    const canViewLuxPlusBenefits = getCanViewLuxPlusBenefits(state)

    dispatch({
      type: API_CALL,
      api: FETCH_LERE_TOP_DEALS,
      request: async() => {
        const { offers, lereVersion } = await RecommendationService.getTopDeals(region, canViewLuxPlusBenefits)

        const leOfferIds = offers.filter(o => o.offer_type && [OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_HOTEL]
          .includes(o.offer_type))
          .map(o => o.offer_id)

        return { offerIds: leOfferIds, lereVersion }
      },
    })
  }
}

export function fetchHighIntentOffers(): AppAction {
  return async(dispatch, getState) => {
    if (!config.LERE_HIGH_INTENT_CAROUSEL_ENABLED) { return }
    if (!canUseConsentPurposeOnClient('personalisation')) { return }

    const state = getState()
    const domainUserId = selectRecDomainUserId(state)
    const leUserId = state.auth.account.memberId
    const highIntent = state.recommendations.highIntent
    const region = state.geo.currentRegionCode

    if (!(domainUserId || leUserId) || highIntent.state === 'done') { return }

    dispatch({
      type: API_CALL,
      api: FETCH_LERE_HIGH_INTENT,
      request: () => RecommendationService.getHighIntentOffers(region, leUserId, domainUserId),
    })
  }
}

export function addAbandonedCart(offerId: string, offerType: App.OfferType, total: number): AppAction {
  return async(dispatch, getState) => {
    const state = getState()
    const userId = getCurrentUserId(state)
    if (!!userId && state.checkout.cart.items.length > 0) {
      const cart: App.AbandonedCartRecommendation = {
        ...state.checkout.cart,
        isDismissed: false,
        offerId,
        offerType,
        creationTime: timeNowInSecond(),
        category: 'sticky_abandoned_carts',
        total,
      }

      dispatch({
        type: LERE_SAVE_ABANDONED_CART,
        cart,
      })
    }
  }
}

/**
 * Should be called after checkout
 */
export function removeAbandonedCartsAndDismissOthers(offerIds: Array<string>): AppAction {
  return async(dispatch, getState) => {
    if (!offerIds.length) { return }

    dispatch({
      type: LERE_REMOVE_ABANDONED_CART,
      offerIds,
    })
    dispatch({
      type: LERE_DISMISS_ALL_STICKY_ABANDONED_CART,
    })
    const state = getState()
    const userId = getCurrentUserId(state)
    const domainUserId = selectRecDomainUserId(state)
    if ((userId || domainUserId) && !isSpoofed(state)) {
      // this need to be awaited to make sure the AC is removed first
      // before updating the others, else there will be a race condition
      await RecommendationService.removeAbandonedCarts(offerIds, domainUserId, userId)
    }
  }
}

export function saveHotLead(hotLead: App.HotLeadOffer): AppAction {
  return (dispatch) => {
    dispatch({
      type: LERE_SAVE_HOT_LEADS,
      hotLead,
    })
  }
}

export function removeHighIntentOffers(offerIds: Array<string>, highIntentCategories: Array<App.HighIntentCategory>): AppAction {
  return async(dispatch, getState) => {
    if (!offerIds.length) { return }

    dispatch({
      type: LERE_REMOVE_HIGH_INTENT,
      offerIds,
      highIntentCategories,
    })

    const state = getState()
    const userId = getCurrentUserId(state)
    const domainUserId = selectRecDomainUserId(state)
    if ((userId || domainUserId) && !isSpoofed(state)) {
      // don't need to await for the response

      if (highIntentCategories.includes('recently_viewed')) {
        RecommendationService.removeRecentlyViewedOffers(offerIds, domainUserId, userId)
      }
      // Hot Leads is the subset of Recent Viewed, will remove both
      if (highIntentCategories.includes('sticky_hot_leads')) {
        RecommendationService.removeHotLeads(offerIds, domainUserId, userId)
        RecommendationService.removeRecentlyViewedOffers(offerIds, domainUserId, userId)
      }
      // Abandoned cart is the subset of Hot Leads and Recent Viewed, will remove all
      if (highIntentCategories.includes('sticky_abandoned_carts')) {
        RecommendationService.removeAbandonedCarts(offerIds, domainUserId, userId)
        RecommendationService.removeHotLeads(offerIds, domainUserId, userId)
        RecommendationService.removeRecentlyViewedOffers(offerIds, domainUserId, userId)
      }
    }
  }
}

export function fetchPersonalisedPreferenceOffers(): AppAction {
  return async(dispatch, getState) => {
    if (!canUseConsentPurposeOnClient('personalisation')) { return }

    const state = getState()
    const leUserId = state.auth.account.memberId
    const currentState = state.recommendations.personalisedPreferences.state
    const region = state.geo.currentRegionCode

    if (!leUserId || currentState === 'loading') { return }

    dispatch({
      type: API_CALL,
      api: FETCH_LERE_PERSONALISED_PREFERENCES,
      request: () => RecommendationService.getPreferenceRecommendations(region, leUserId),
    })
  }
}

export function getTourKey(
  offerId: string,
  region: string,
  departureYear?: number,
  departureMonth?: number,
) {
  if (!!departureYear && departureMonth !== undefined) {
    return `${offerId}-${region}-${departureYear}-${departureMonth + 1}`
  }
  return `${offerId}-${region}`
}

export function fetchRecommendedTours(
  offerId: string,
  departureYear?: number,
  departureMonth?: number,
  offerType?: Array<App.Tours.V2ProductType>,
): AppAction {
  return (dispatch, getState) => {
    const state: App.State = getState()
    const region = state.geo.currentRegionCode

    const key = getTourKey(offerId, region, departureYear, departureMonth)
    const tourState = state.recommendations.tour[key]

    if (tourState?.state == 'done' || tourState?.state == 'loading') { return }

    dispatch({
      type: API_CALL,
      api: FETCH_LERE_TOUR_RECOMMENDATIONS,
      request: () => RecommendationService.getRecommendedTours(offerId, region, departureYear, departureMonth, offerType),
      key,
    })
  }
}

export function fetchUrgentOfferRoomDates(offerId: string, roomTypeId: string, checkIn: string, checkOut: string): AppAction {
  return async(dispatch, getState) => {
    const state: App.State = getState()

    const cacheKey = `${offerId}_${roomTypeId}_${checkIn}_${checkOut}`

    if (state.recommendations.urgentOfferRoomDates.data[cacheKey]?.roomsRemaining) {
      return
    }
    dispatch({
      type: API_CALL,
      api: FETCH_URGENT_OFFER_ROOM_DATES,
      key: 'urgentRoomDates',
      cacheKey,
      request: () => RecommendationService.getUrgentOfferRoomDates(offerId, roomTypeId, checkIn, checkOut),
    })
  }
}

export function fetchLatestRestorableCheckoutCart(): AppAction {
  return async(dispatch, getState) => {
    const state = getState()
    const memberId = getCurrentUserId(state)

    if (!memberId) return

    const cacheKey = `${memberId}_${config.BRAND}`
    const current = state.recommendations.latestRestorableCheckoutCart

    if (current.state === 'loading') return

    dispatch({
      type: API_CALL,
      api: FETCH_LATEST_RESTORABLE_CHECKOUT_CART,
      cacheKey,
      request: () => getLatestRestorableCheckoutCart().then(cart => ({ cart })),
    })
  }
}

export function fetchUserInteractions(interactionType: App.UserInteractionType): AppAction {
  return async(dispatch, getState) => {
    if (!canUseConsentPurposeOnClient('personalisation')) { return }

    const state = getState()
    const currentState = state.recommendations.userInteractions[interactionType]

    if (currentState?.state === 'done') { return }

    const domainUserId = selectRecDomainUserId(state)
    const leUserId = getCurrentUserId(state)

    if (!domainUserId && !leUserId) return

    dispatch({
      type: API_CALL,
      api: FETCH_LERE_USER_INTERACTIONS,
      interactionType,
      request: () => RecommendationService.getUserInteractions(interactionType, leUserId, domainUserId),
    })
  }
}
